import { newProgressId, retryNotice, watchProgress, type RetryNotice } from '@dbrain/app-core/skill-progress';
import type { SkillProgressDto } from '@dbrain/shared';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client.js';

/**
 * Phase 16c.6 (D-433): while "Skill ekle" (or "Kaynaktan güncelle") waits for a source, what the server says it is
 * doing: `begin()` gives the look its progress id and starts asking (after 2 s, then every second), `end()` stops.
 * The note shows only while the server is trying a site once more.
 */
export function useSkillProgress(): { notice: RetryNotice | null; begin: () => string; end: () => void } {
  const [id, setId] = useState<string | null>(null);
  const [progress, setProgress] = useState<SkillProgressDto | null>(null);
  useEffect(() => {
    if (!id) return undefined;
    return watchProgress(() => api<SkillProgressDto>('GET', `/api/skills/progress/${id}`), setProgress);
  }, [id]);
  const begin = useCallback(() => {
    const next = newProgressId();
    setProgress(null);
    setId(next);
    return next;
  }, []);
  const end = useCallback(() => {
    setId(null);
    setProgress(null);
  }, []);
  return { notice: id ? retryNotice(progress) : null, begin, end };
}

export function SkillRetryNote({ notice }: { notice: RetryNotice | null }) {
  const { t } = useTranslation();
  // the live region stays in place so that a screen reader hears the note when it comes
  return (
    <p className="skill-retry-note" role="status" aria-live="polite" data-testid="skill-retrying">
      {notice ? (
        <>
          <span className="ui-spinner ui-spinner--inline" aria-hidden="true" />
          {notice.kind === 'github' ? t('skills.add.retrying.github') : t('skills.add.retrying.link', { host: notice.host })}
        </>
      ) : null}
    </p>
  );
}
